import { useEffect, type Dispatch, type SetStateAction, type MutableRefObject } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import type { Compose } from "./types";
type InquiryInput = {
 search: string; user: ReturnType<typeof useAuth>["user"];
 openThread: (id: string, starter?: string, context?: Compose | null) => Promise<void>;
 conversationRequestRef: MutableRefObject<number>;
 setCompose: Dispatch<SetStateAction<Compose | null>>; setDraft: Dispatch<SetStateAction<string>>;
 setActiveId: Dispatch<SetStateAction<string | null>>;
 setConvo: Dispatch<SetStateAction<Awaited<ReturnType<typeof api.getThread>> | null>>;
};
export function useMessageInquiry(input: InquiryInput) {
 const {search,user,openThread,conversationRequestRef,setCompose,setDraft,setActiveId,setConvo} = input;
  // ?to=<id>&type=<ctx>&id=<ctxId>&name=<label>&intent=<hire|quote|bid_negotiation> opens a compose pane
  useEffect(() => {
    if (!user) return;
    const sp = new URLSearchParams(search);
    const to = Number(sp.get("to"));
    if (!(to > 0)) return;
    const ctxType = sp.get("type") || "direct";
    const ctxId = sp.get("id") || "";
    let cancelled = false;
    (async () => {
      // [messaging.contact-and-context](../../../../docs/product/messaging-and-notifications.md#messaging-contact-and-context): company inquiries compose through POST /threads, which pins
      // both profiles. The inbox projection cannot distinguish a matching
      // owner profile from another business owned by the same person.
      const matches = (list: Awaited<ReturnType<typeof api.listThreads>>["threads"]) => list.find((t) => t.otherUserId === to &&
        ctxType !== "company" && !t.companyId && t.contextType !== "company");
      const active = await api.listThreads("active").then((r) => r?.threads ?? []).catch(() => []);
      let existing = matches(active);
      if (!existing) {
        const arch = await api.listThreads("archived").then((r) => r?.threads ?? []).catch(() => []);
        existing = matches(arch);
      }
      if (cancelled) return;
      const intent = sp.get("intent") || "";
      const name = sp.get("name") || undefined;
      const subject = sp.get("subject") || (intent === "hire" ? "Hiring inquiry" : intent === "quote" ? "Quote request" : undefined);
      const starter = intent === "bid_negotiation"
        ? `Hi${name ? ` ${name}` : ""}, I saw your bid${subject ? ` for "${subject}"` : ""}. Are you open to discussing the price or terms? If we agree on changes, please update your bid before I award the project.`
        : intent === "quote" && ctxType === "service" ? "Here are the details of what I need:\n\n• "
        : intent === "quote" && ctxType === "item" ? `Hi, I would like a sale quotation${subject && subject !== "Quote request" ? ` for ${subject}` : ""}. Please include the assets, exclusions, transfer timing and payment milestones. My requested terms:\n\n• `
        : intent === "quote" ? `Hi${name ? " " + name : ""}, I’d like to request a quote${subject && subject !== "Quote request" ? ` for ${subject}` : ""}. Here are the details of what I need:\n\n• ` : "";
      const context = { toUserId: to, contextType: ctxType, contextId: ctxId, subject, toName: name,
        requestQuote: intent === "quote" && (ctxType === "service" || ctxType === "item"), bidId: sp.get("bidId") || undefined };
      if (existing) { openThread(existing.id, starter, context); return; }
      ++conversationRequestRef.current;
      setCompose(context);
      // helpful starter message for hire/quote intents
      if (intent === "hire") setDraft(`Hi${name ? " " + name : ""}, I'd like to hire you for a job. Are you available to discuss the details?`);
      else if (intent === "quote") setDraft(starter);
      else if (starter) setDraft(starter);
      setActiveId(null); setConvo(null);
    })();
    return () => { cancelled = true; };
  }, [search, user?.id]);

}
